// src/utils/password-toggle.ts

// Wording follows the web.dev sign-in form guidance: the "show" label warns the
// user that the secret becomes visible on screen.
const SHOW_LABEL = 'Show password as plain text. Warning: this will display your password on the screen.';
const HIDE_LABEL = 'Hide password.';

const WRAPPER_SELECTOR = '[data-scw-password-toggle]';
const BUTTON_SELECTOR = 'button.scw-password-toggle';

const attachToggle = (wrapper: HTMLElement, template: HTMLTemplateElement) => {
    // Idempotent: safe to call initPasswordToggles twice on the same root.
    if (wrapper.querySelector(BUTTON_SELECTOR)) return;

    const input = wrapper.querySelector('input');
    if (!input) {
        console.warn('PasswordToggle: no input found inside a [data-scw-password-toggle] wrapper.');
        return;
    }

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const button = fragment.querySelector(BUTTON_SELECTOR) as HTMLButtonElement | null;
    const icon = button?.querySelector('i') as HTMLElement | null;
    if (!button || !icon) {
        console.warn('PasswordToggle: template_passwordToggle is missing its button or icon.');
        return;
    }

    // Appended after the input so tab order is type-then-reveal.
    wrapper.append(button);

    // Keeps focus in the input on mouse activation. Safari does not focus a
    // <button> on click while Chrome and Firefox do, so without this the caret
    // restore below would behave differently per browser.
    button.addEventListener('mousedown', (event) => event.preventDefault());

    button.addEventListener('click', () => {
        const {selectionStart, selectionEnd} = input;
        const hadFocus = document.activeElement === input;
        const reveal = input.type === 'password';

        input.type = reveal ? 'text' : 'password';

        icon.classList.toggle('fa-eye', !reveal);
        icon.classList.toggle('fa-eye-slash', reveal);
        button.setAttribute('aria-label', reveal ? HIDE_LABEL : SHOW_LABEL);

        // Only pull focus back if it was already in the input — a keyboard user
        // who tabbed to the button must be able to keep tabbing forward.
        if (hadFocus) input.focus();

        // Chrome keeps the caret across a type change; WebKit and Gecko move it
        // to the end, so restore it unconditionally.
        if (selectionStart !== null) input.setSelectionRange(selectionStart, selectionEnd);
    });
};

// Wires up every password field under `root`. Accepts a DocumentFragment so it
// can run before the markup is appended to the document.
export function initPasswordToggles(root: ParentNode) {
    const wrappers = root.querySelectorAll<HTMLElement>(WRAPPER_SELECTOR);
    if (!wrappers.length) return;

    const template = document.getElementById('template_passwordToggle') as HTMLTemplateElement | null;
    if (!template) {
        console.warn('PasswordToggle: template_passwordToggle not found in DOM.');
        return;
    }

    wrappers.forEach((wrapper) => attachToggle(wrapper, template));
}
